<template>
  <div v-preline class="h-full">
    <MainLayout>
      <router-view v-slot="{ Component, route }">
        <transition :name="getTransitionName(route, previousRoute)" mode="out-in">
          <div :key="route.fullPath" class="route-wrapper">
            <component :is="Component" />
          </div>
        </transition>
      </router-view>
    </MainLayout>
  </div>
</template>

<script setup lang="ts">
import MainLayout from '@/components/layout/MainLayout.vue'
import { useAuthStore } from '@/stores/authStore'
import { debouncedInitializePreline } from '@/utils/preline-manager'
import { useRedirectAfterLogin } from '@/utils/route'
import { useI18n } from '@/composables/useI18n'
import { onMounted, watch, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { hasPendingRedirect, performRedirect } = useRedirectAfterLogin()
const { initializeLanguage } = useI18n()

// Track previous route for transition detection
const previousRoute = ref<any>(null)

// Transition names based on route changes
const getTransitionName = (currentRoute: any, fromRoute?: any) => {
  // No transitions for tab switching within public-pool
  if (
    fromRoute &&
    currentRoute.name === 'public-pool' &&
    fromRoute.name === 'public-pool' &&
    currentRoute.path === fromRoute.path
  ) {
    // Same route, only query changed (tab switching) - no transition
    return 'none'
  }

  // Customize transitions based on route meta or path
  if (currentRoute.meta?.transition) {
    return currentRoute.meta.transition
  }

  // Default transitions for different route types
  if (currentRoute.path.startsWith('/public-pool')) {
    return 'slide'
  }

  if (currentRoute.meta?.layout === 'auth') {
    return 'fade'
  }

  return 'fade'
}

onMounted(async () => {
  // Initialize validation i18n
  initializeLanguage()

  // Check authentication status on app initialization only if not already checked
  if (!authStore.sessionReady) {
    await authStore.checkAuth()
  }

  // Handle pending redirects after login
  if (authStore.isAuthenticated && hasPendingRedirect.value) {
    await performRedirect(router)
  }
})

// Reinitialize Preline components on route change
watch(
  () => route.path,
  () => {
    debouncedInitializePreline()
  }
)

// Track route changes for transition detection
watch(
  () => route.fullPath,
  (newPath, oldPath) => {
    if (oldPath) {
      // Store previous route info
      previousRoute.value = {
        name: route.name,
        path: oldPath.split('?')[0], // Get path without query
        query: route.query
      }
    }
  }
)

// Watch for successful authentication to handle redirects
watch(
  () => authStore.isAuthenticated,
  (isAuthenticated) => {
    if (isAuthenticated && hasPendingRedirect.value) {
      performRedirect(router)
    }
  }
)

// Update document title from route meta with i18n support
watch(
  () => route.meta.title,
  (title) => {
    if (title) {
      document.title = `${title} | ${import.meta.env.VITE_APP_NAME || 'Mining Pool'}`
    } else {
      document.title = import.meta.env.VITE_APP_NAME || 'Mining Pool'
    }
  },
  { immediate: true }
)
</script>
